// "Cobrar por pessoa" no Fechar mesa (dono, 24/09/2026): o caixa toca no nome
// e os produtos dessa pessoa ficam marcados; ao confirmar o pagamento (dinheiro,
// cartão...) a saída dela é dada ali mesmo — no caixa não tem QR de saída.
// Também mantém a janela em dia com o celular: o que foi pago pelo QR fica
// verde, os PIX entram no log do fechamento e produto cancelado sai da lista.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { pagoParcialItem } from "../mesas-fechamento";
import { saidaNoCaixa } from "./api";
import { usePessoasMesa } from "./usePessoasMesa";

const SEM_DONO = "__mesa__";
// Mesma chave do Fechar mesa: cartItemId ou "i<posição sem os riscados>"
const chaveDe = (item) => String(item.cartItemId || `i${item._idx}`);

export function useCobrancaPorPessoa(mesa, aberto, { itens, setItens, setLog, onRemover }) {
  const { pessoas, pagos, logQr, removidos, donos, saidaLigada, recarregar, invalidar, pausar } = usePessoasMesa(mesa, aberto);
  // Dono do produto: o gravado no item ou, no item antigo do QR, o que o
  // servidor achou pelo pedido da rodada. Sem dono = "Mesa".
  const donoDe = useCallback((item) => item._cliente_id || donos[chaveDe(item)] || null, [donos]);
  const [pessoaId, setPessoaId] = useState(null);
  const [darSaida, setDarSaida] = useState(true);
  const itensRef = useRef(itens);
  itensRef.current = itens;
  const pessoaRef = useRef(null);
  pessoaRef.current = pessoas.find((p) => p.cliente_id === pessoaId) || null;

  // Pago pelo celular (ou em outro caixa) com esta janela aberta: marca na
  // hora; rodada cancelada / item riscado sai da lista
  useEffect(() => {
    if (!pagos.length && !removidos.length) return;
    const porChave = new Map(pagos.map((p) => [String(p.chave), p.label]));
    const fora = new Set(removidos.map(String));
    let mudou = false;
    let pagoSelecionado = false;
    let canceladoSelecionado = false;
    const tirados = new Set();
    const novo = [];
    for (const item of itensRef.current) {
      // Cancelado (rodada do QR, item riscado em outro aparelho) sai da lista —
      // a não ser que já tenha pagamento parcial: aí fica, para o recebido
      // continuar abatendo da conta
      if (!item._paid && item.cartItemId != null && fora.has(String(item.cartItemId)) && !(pagoParcialItem(item) > 0.009)) {
        mudou = true;
        canceladoSelecionado = canceladoSelecionado || !!item._selected;
        tirados.add(String(item.cartItemId));
        continue;
      }
      if (!item._paid && porChave.has(chaveDe(item))) {
        mudou = true;
        pagoSelecionado = pagoSelecionado || !!item._selected;
        novo.push({ ...item, _paid: true, _selected: false, _selQtd: null, _paymentInfo: porChave.get(chaveDe(item)) || "Pago" });
        continue;
      }
      novo.push(item);
    }
    if (!mudou) return;
    setItens(novo);
    // A mesa do Fechar mesa também perde o item: é dela que sai o Total a pagar
    if (tirados.size) onRemover?.(tirados);
    if (pagoSelecionado || canceladoSelecionado) {
      // Não deixa o total virar "o resto da mesa" sem o caixa perceber
      const nome = pessoaRef.current?.primeiro_nome || pessoaRef.current?.nome;
      setPessoaId(null);
      toast.info(pagoSelecionado
        ? (nome ? `${nome} acabou de pagar pelo celular. Confira antes de cobrar.` : "Produtos marcados acabaram de ser pagos pelo celular. Confira antes de cobrar.")
        : "Produtos marcados foram cancelados e saíram da conta. Confira antes de cobrar.", { duration: 8000 });
    }
  }, [pagos, removidos, setItens, onRemover]);

  // Os PIX do celular entram no log do fechamento (forma e total da conta)
  useEffect(() => {
    if (!logQr.length || !setLog) return;
    setLog((prev) => {
      const vistos = new Set((prev || []).map((l) => l && l.pagamento_id).filter(Boolean));
      const novos = logQr.filter((l) => l.pagamento_id && !vistos.has(l.pagamento_id));
      return novos.length ? [...(prev || []), ...novos] : prev;
    });
  }, [logQr, setLog]);

  const escolher = useCallback((id) => {
    const nova = pessoaId === id ? null : id;
    setPessoaId(nova);
    setItens((prev) => prev.map((item) => {
      if (item._paid) return item;
      const dono = donoDe(item) || SEM_DONO;
      return { ...item, _selected: !!nova && dono === nova, _selQtd: null };
    }));
  }, [pessoaId, setItens, donoDe]);

  const limpar = useCallback(() => setPessoaId(null), []);

  // Logo antes de cobrar: algum produto ainda aberto nesta tela acabou de ser
  // pago pelo celular, ou saiu da conta? (o valor a cobrar mudou)
  const pagosAgora = useCallback(async (lista) => {
    if (!pessoas.length) return [];
    const dados = await recarregar();
    const chaves = new Set((dados?.pagos || []).map((p) => String(p.chave)));
    const fora = new Set((dados?.removidos || []).map(String));
    return lista.filter((i) => !i._paid && (chaves.has(chaveDe(i)) || (i.cartItemId != null && fora.has(String(i.cartItemId)))));
  }, [pessoas.length, recarregar]);

  const darSaidaDireto = useCallback(async (pessoa) => {
    if (!saidaLigada) return;
    try {
      const { data } = await saidaNoCaixa(mesa.id, pessoa.cliente_id);
      toast.success(data?.mesa_fechou ? "Todos saíram — mesa fechada." : `Saída de ${pessoa.primeiro_nome || pessoa.nome} liberada.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.mensagem || "Não consegui dar a saída.");
    }
    recarregar();
  }, [mesa?.id, recarregar, saidaLigada]);

  // Chamado depois que o pagamento parcial foi salvo na mesa
  const aposPagamento = useCallback(async (itensAtualizados) => {
    const id = pessoaId;
    setPessoaId(null);
    if (!id || id === SEM_DONO || !darSaida || !saidaLigada || !mesa?.id) return;
    const pessoa = pessoas.find((p) => p.cliente_id === id);
    if (!pessoa || pessoa.saiu) return;
    const falta = itensAtualizados.some((i) => donoDe(i) === id && !i._paid);
    if (falta) return;
    try {
      const { data } = await saidaNoCaixa(mesa.id, id);
      toast.success(data?.mesa_fechou ? "Todos saíram — mesa fechada." : `Saída de ${pessoa.primeiro_nome || pessoa.nome} liberada.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.mensagem || "Pagamento salvo, mas não consegui dar a saída. Libere pelo QR dela.");
    }
    recarregar();
  }, [pessoaId, darSaida, saidaLigada, mesa?.id, pessoas, recarregar, donoDe]);

  return {
    pessoas, pessoaId, escolher, limpar, darSaida, setDarSaida, aposPagamento, pagosAgora, darSaidaDireto,
    invalidar, pausar, recarregar, donoDe, donos, saidaLigada, SEM_DONO,
  };
}
